import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import { Home, Sparkles, Settings, Plus, Camera, PenLine } from "lucide-react";

const item = "flex flex-col items-center gap-1 text-[11px] font-medium tracking-wide";

export function BottomNav() {
  const navigate = useNavigate();
  const { t } = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const chooseScan = () => {
    setMenuOpen(false);
    navigate("/add");
  };

  const chooseManual = () => {
    setMenuOpen(false);
    // Dashboard listens for this event to open the manual drawer.
    // If user is on another route, we still redirect to dashboard with ?manual=1.
    if (window.location.pathname === "/dashboard") {
      window.dispatchEvent(new CustomEvent("pantry:manual-add"));
    } else {
      navigate("/dashboard?manual=1");
    }
  };

  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md backdrop-blur-2xl bg-white/85 border-t border-zinc-200/70 pb-safe z-30"
      data-testid="bottom-nav"
    >
      <div className="relative flex justify-around items-center h-20 px-4">
        <NavLink
          to="/dashboard"
          end
          className={({ isActive }) => `${item} ${isActive ? "text-zinc-950" : "text-zinc-400"}`}
          data-testid="nav-home"
        >
          {({ isActive }) => (
            <>
              <Home className="w-5 h-5" strokeWidth={isActive ? 2.4 : 2} />
              <span>{t("nav.home")}</span>
              {isActive && <span className="w-1 h-1 rounded-full bg-fresh-green" />}
            </>
          )}
        </NavLink>

        {/* FAB with menu (Scansiona / Manuale) */}
        <div ref={menuRef} className="relative">
          {menuOpen && (
            <>
              {/* Backdrop scrim to dim the app without blocking the menu */}
              <div
                className="fixed inset-0 bg-black/30 -z-10 animate-fade-in"
                aria-hidden="true"
                onClick={() => setMenuOpen(false)}
                data-testid="fab-backdrop"
              />
              <div
                className="absolute bottom-[72px] left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-2xl shadow-black/30 border border-zinc-200 w-60 overflow-hidden"
                data-testid="fab-menu"
                role="menu"
              >
                <button
                  type="button"
                  onClick={chooseScan}
                  className="w-full px-4 py-4 flex items-center gap-3 hover:bg-zinc-50 active:bg-zinc-100 transition text-left"
                  data-testid="fab-scan-btn"
                  role="menuitem"
                >
                  <span className="w-10 h-10 rounded-xl bg-fresh-green-soft flex items-center justify-center">
                    <Camera className="w-5 h-5 text-fresh-green-text" strokeWidth={2.3} />
                  </span>
                  <span className="flex-1">
                    <span className="block font-heading text-base font-medium text-zinc-950">{t("fab.scan")}</span>
                    <span className="block text-xs text-zinc-500">{t("fab.scan.desc")}</span>
                  </span>
                </button>
                <div className="h-px bg-zinc-100" />
                <button
                  type="button"
                  onClick={chooseManual}
                  className="w-full px-4 py-4 flex items-center gap-3 hover:bg-zinc-50 active:bg-zinc-100 transition text-left"
                  data-testid="fab-manual-btn"
                  role="menuitem"
                >
                  <span className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center">
                    <PenLine className="w-5 h-5 text-zinc-800" strokeWidth={2.3} />
                  </span>
                  <span className="flex-1">
                    <span className="block font-heading text-base font-medium text-zinc-950">{t("fab.manual")}</span>
                    <span className="block text-xs text-zinc-500">{t("fab.manual.desc")}</span>
                  </span>
                </button>
              </div>
            </>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`relative -top-6 w-16 h-16 rounded-full bg-zinc-950 text-white shadow-xl shadow-zinc-900/25 flex items-center justify-center active:scale-95 transition ${menuOpen ? "rotate-45" : ""}`}
            style={{ transitionProperty: "transform" }}
            aria-label="Aggiungi prodotto"
            aria-expanded={menuOpen}
            data-testid="add-item-fab"
          >
            <Plus className="w-7 h-7" strokeWidth={2.5} />
          </button>
        </div>

        <NavLink
          to="/suggestions"
          className={({ isActive }) => `${item} ${isActive ? "text-zinc-950" : "text-zinc-400"}`}
          data-testid="nav-suggestions"
        >
          {({ isActive }) => (
            <>
              <Sparkles className="w-5 h-5" strokeWidth={isActive ? 2.4 : 2} />
              <span>{t("nav.suggestions")}</span>
              {isActive && <span className="w-1 h-1 rounded-full bg-fresh-green" />}
            </>
          )}
        </NavLink>

        <NavLink
          to="/settings"
          className={({ isActive }) => `${item} ${isActive ? "text-zinc-950" : "text-zinc-400"}`}
          data-testid="nav-settings"
        >
          {({ isActive }) => (
            <>
              <Settings className="w-5 h-5" strokeWidth={isActive ? 2.4 : 2} />
              <span>{t("nav.settings")}</span>
              {isActive && <span className="w-1 h-1 rounded-full bg-fresh-green" />}
            </>
          )}
        </NavLink>
      </div>
    </nav>
  );
}

export default BottomNav;
